/**
 * UnreadCountsContext
 * 
 * Context để share unread counts state giữa các components
 * Đảm bảo UnifiedChatList và SimpleChatBox cùng sử dụng một instance của useUnreadCounts
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useUnreadCounts } from 'hooks/useUnreadCounts';

import { authenticateSettings } from 'components/helper/appConfig';
import { getAccessToken, getUserId } from 'utils/sessionStorage';

interface UnreadCountsContextType {
  unreadCounts: { [conversationId: string]: number };
  getUnreadCount: (conversationId: string) => number;
  decreaseUnreadCount: (conversationId: string, amount?: number) => void;
  fetchAllUnreadMessages: (fromIds?: number[]) => Promise<void>;
}

const UnreadCountsContext = createContext<UnreadCountsContextType | undefined>(undefined);

export const UnreadCountsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const userId = Number(getUserId());
  const token = getAccessToken();
  
  // Only enable unread counts when user is logged in and has a token
  const isAuthenticated = !!token && !!userId;
  
  const unreadCountsHook = useUnreadCounts({
    userId,
    enabled: isAuthenticated,
  });

  return (
    <UnreadCountsContext.Provider value={unreadCountsHook}>
      {children}
    </UnreadCountsContext.Provider>
  );
};

export const useUnreadCountsContext = () => {
  const context = useContext(UnreadCountsContext);
  if (context === undefined) {
    throw new Error('useUnreadCountsContext must be used within an UnreadCountsProvider');
  }
  return context;
};
